import { useEffect, useRef } from 'react';

const NIGHT_BG =
  'linear-gradient(to bottom, #04040f 0%, #0a0e26 35%, #15143a 65%, #2a1748 88%, #3a1a4a 100%)';

const STAR_COUNT = 1200;
// Stars rotate 360° in ~32 minutes (1° every ~5.36s).
const STAR_ROTATION_MS = (30 * 360 * 1000) / 8 / 0.7;

const COMET_VISIBLE_MS = 2500;
const COMET_INTERVAL_MS = 35000;
const FIRST_COMET_MIN_MS = 5000;
const FIRST_COMET_MAX_MS = 15000;

interface Comet {
  startX: number;
  startY: number;
  dxPx: number;
  dyPx: number;
  startedAt: number;
}

function makeRand(seed = 0x9e3779b9) {
  let s = seed;
  return () => {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Constellations — only the star *positions* are drawn. They're rendered in
// the same style as the random background stars, so the shapes are hidden in
// plain sight. Coords are normalized to a unit box per constellation.
interface ConstStar {
  x: number;
  y: number;
}
interface Constellation {
  name: string;
  stars: ConstStar[];
  // Placement on the offscreen star canvas, in fractions of starSize.
  cx: number;
  cy: number;
  // Width of the constellation's bounding box in fractions of starSize.
  size: number;
}

const CONSTELLATIONS: Constellation[] = [
  // Big Dipper (Ursa Major)
  {
    name: 'Ursa Major',
    cx: 0.38,
    cy: 0.38,
    size: 0.1,
    stars: [
      { x: 0.92, y: 0.2 },   // Dubhe
      { x: 0.92, y: 0.45 },  // Merak
      { x: 0.7, y: 0.5 },    // Phecda
      { x: 0.7, y: 0.27 },   // Megrez
      { x: 0.5, y: 0.32 },   // Alioth
      { x: 0.27, y: 0.42 },  // Mizar
      { x: 0.06, y: 0.6 },   // Alkaid
    ],
  },
  // Orion
  {
    name: 'Orion',
    cx: 0.59,
    cy: 0.59,
    size: 0.1,
    stars: [
      { x: 0.68, y: 0.18 }, // Betelgeuse
      { x: 0.32, y: 0.25 }, // Bellatrix
      { x: 0.6, y: 0.5 },   // Alnitak
      { x: 0.5, y: 0.52 },  // Alnilam
      { x: 0.4, y: 0.54 },  // Mintaka
      { x: 0.7, y: 0.86 },  // Saiph
      { x: 0.27, y: 0.86 }, // Rigel
    ],
  },
  // Cassiopeia (W)
  {
    name: 'Cassiopeia',
    cx: 0.59,
    cy: 0.41,
    size: 0.08,
    stars: [
      { x: 0.05, y: 0.55 },
      { x: 0.27, y: 0.15 },
      { x: 0.5, y: 0.5 },
      { x: 0.73, y: 0.18 },
      { x: 0.95, y: 0.5 },
    ],
  },
  // Lyra (parallelogram + Vega)
  {
    name: 'Lyra',
    cx: 0.31,
    cy: 0.61,
    size: 0.06,
    stars: [
      { x: 0.5, y: 0.05 },
      { x: 0.28, y: 0.4 },
      { x: 0.72, y: 0.4 },
      { x: 0.3, y: 0.85 },
      { x: 0.7, y: 0.85 },
    ],
  },
  // Cygnus (Northern Cross)
  {
    name: 'Cygnus',
    cx: 0.5,
    cy: 0.37,
    size: 0.08,
    stars: [
      { x: 0.5, y: 0.05 },  // Deneb (tail)
      { x: 0.5, y: 0.4 },   // upper body
      { x: 0.5, y: 0.6 },   // Sadr (center)
      { x: 0.5, y: 0.95 },  // Albireo (beak)
      { x: 0.12, y: 0.55 }, // left wingtip
      { x: 0.32, y: 0.58 }, // left wing
      { x: 0.7, y: 0.62 },  // right wing
      { x: 0.9, y: 0.65 },  // right wingtip
    ],
  },
  // Scorpius (curving body + stinger)
  {
    name: 'Scorpius',
    cx: 0.5,
    cy: 0.63,
    size: 0.1,
    stars: [
      { x: 0.08, y: 0.18 }, // Graffias / head
      { x: 0.18, y: 0.22 },
      { x: 0.28, y: 0.28 },
      { x: 0.4, y: 0.36 },  // Antares (heart)
      { x: 0.5, y: 0.5 },
      { x: 0.58, y: 0.65 },
      { x: 0.66, y: 0.78 },
      { x: 0.78, y: 0.86 },
      { x: 0.9, y: 0.78 },  // Shaula (stinger)
      { x: 0.95, y: 0.7 },  // Lesath
    ],
  },
  // Leo (sickle + triangle)
  {
    name: 'Leo',
    cx: 0.37,
    cy: 0.5,
    size: 0.08,
    stars: [
      // sickle (head/mane)
      { x: 0.18, y: 0.65 }, // Regulus
      { x: 0.22, y: 0.5 },
      { x: 0.18, y: 0.32 },
      { x: 0.1, y: 0.2 },
      { x: 0.05, y: 0.32 },
      { x: 0.1, y: 0.5 },
      // hindquarters triangle
      { x: 0.55, y: 0.45 },
      { x: 0.92, y: 0.55 },
      { x: 0.7, y: 0.72 },
    ],
  },
  // Gemini (twin stick figures)
  {
    name: 'Gemini',
    cx: 0.63,
    cy: 0.5,
    size: 0.07,
    stars: [
      // Castor
      { x: 0.2, y: 0.08 },
      { x: 0.25, y: 0.38 },
      { x: 0.27, y: 0.65 },
      { x: 0.18, y: 0.95 },
      { x: 0.34, y: 0.92 },
      // Pollux
      { x: 0.68, y: 0.16 },
      { x: 0.6, y: 0.45 },
      { x: 0.6, y: 0.72 },
      { x: 0.5, y: 0.95 },
      { x: 0.7, y: 0.94 },
    ],
  },
  // Pegasus (Great Square)
  {
    name: 'Pegasus',
    cx: 0.5,
    cy: 0.5,
    size: 0.07,
    stars: [
      { x: 0.15, y: 0.15 }, // Scheat
      { x: 0.85, y: 0.18 }, // Markab
      { x: 0.88, y: 0.85 }, // Algenib
      { x: 0.18, y: 0.85 }, // Alpheratz
      { x: 0.0, y: 0.5 },   // neck/head extension
      { x: 0.95, y: 0.45 },
    ],
  },
  // Crux (Southern Cross)
  {
    name: 'Crux',
    cx: 0.5,
    cy: 0.28,
    size: 0.05,
    stars: [
      { x: 0.5, y: 0.05 },  // Acrux (top)
      { x: 0.5, y: 0.95 },  // Gacrux (bottom)
      { x: 0.08, y: 0.55 }, // Mimosa (left)
      { x: 0.92, y: 0.5 },  // Becrux (right)
      { x: 0.42, y: 0.62 }, // ε Crucis
    ],
  },
  // Boötes (kite + Arcturus)
  {
    name: 'Boötes',
    cx: 0.41,
    cy: 0.59,
    size: 0.07,
    stars: [
      { x: 0.5, y: 0.95 },  // Arcturus (bottom of kite)
      { x: 0.25, y: 0.7 },
      { x: 0.5, y: 0.4 },
      { x: 0.75, y: 0.7 },
      { x: 0.32, y: 0.18 },
      { x: 0.7, y: 0.2 },
    ],
  },
  // Andromeda (line of stars)
  {
    name: 'Andromeda',
    cx: 0.69,
    cy: 0.61,
    size: 0.07,
    stars: [
      { x: 0.05, y: 0.6 },
      { x: 0.25, y: 0.55 },
      { x: 0.45, y: 0.5 },
      { x: 0.65, y: 0.42 },
      { x: 0.85, y: 0.35 },
      { x: 0.95, y: 0.55 },
      { x: 0.55, y: 0.7 },
    ],
  },
];

function paintConstellations(ctx: CanvasRenderingContext2D, size: number) {
  // Use a dedicated seeded RNG so each constellation star gets a stable size
  // and opacity within the same range as the background field — but a keen
  // observer can still tell because the dots align.
  const rand = makeRand(0x517cc1b7);
  ctx.fillStyle = '#ffffff';
  for (const c of CONSTELLATIONS) {
    const boxX = c.cx * size - (c.size * size) / 2;
    const boxY = c.cy * size - (c.size * size) / 2;
    const boxW = c.size * size;
    for (const s of c.stars) {
      const x = boxX + s.x * boxW;
      const y = boxY + s.y * boxW;
      const r = (rand() < 0.85 ? 1 + rand() * 1.2 : 2 + rand() * 1.4) / 2;
      const opacity = 0.4 + rand() * 0.55;
      ctx.globalAlpha = opacity;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

function paintConstellationsRevealed(ctx: CanvasRenderingContext2D, size: number) {
  // A subtle "reveal" overlay — slightly larger cores plus a soft halo so the
  // constellation stars become legible without exploding into beacons.
  for (const c of CONSTELLATIONS) {
    const boxX = c.cx * size - (c.size * size) / 2;
    const boxY = c.cy * size - (c.size * size) / 2;
    const boxW = c.size * size;
    for (const s of c.stars) {
      const x = boxX + s.x * boxW;
      const y = boxY + s.y * boxW;
      const halo = 5;
      const grad = ctx.createRadialGradient(x, y, 0, x, y, halo);
      grad.addColorStop(0, 'rgba(255,255,255,0.8)');
      grad.addColorStop(0.4, 'rgba(220,230,255,0.28)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, halo, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(x, y, 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * Draws constellation names upright at their current rotated positions.
 * Called from the main render loop after the rotated star layers are drawn,
 * so the labels track the constellations spatially without spinning.
 */
function paintConstellationNames(
  ctx: CanvasRenderingContext2D,
  viewW: number,
  viewH: number,
  starSize: number,
  angle: number,
  alpha: number,
) {
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);
  ctx.save();
  ctx.font = '500 12px ui-sans-serif, system-ui, -apple-system, "Nunito Sans", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.shadowColor = 'rgba(0,0,0,0.7)';
  ctx.shadowBlur = 4;
  ctx.fillStyle = `rgba(225,235,255,${0.78 * alpha})`;
  for (const c of CONSTELLATIONS) {
    // Label anchor point in the (un-rotated) star canvas coordinate system,
    // centered on rotation origin.
    const localX = (c.cx - 0.5) * starSize;
    const localY = (c.cy + c.size / 2 - 0.5) * starSize + 14;
    const screenX = viewW / 2 + localX * cosA - localY * sinA;
    const screenY = viewH / 2 + localX * sinA + localY * cosA;
    ctx.fillText(c.name, screenX, screenY);
  }
  ctx.restore();
}

function paintStarField(ctx: CanvasRenderingContext2D, size: number) {
  const rand = makeRand();
  ctx.fillStyle = '#ffffff';
  for (let i = 0; i < STAR_COUNT; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const r = (rand() < 0.85 ? 1 + rand() * 1.2 : 2 + rand() * 1.4) / 2;
    const opacity = 0.4 + rand() * 0.55;
    ctx.globalAlpha = opacity;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  paintConstellations(ctx, size);
}

function makeComet(now: number): Comet {
  const W = window.innerWidth;
  const H = window.innerHeight;
  const fromLeft = Math.random() < 0.5;
  const startY = (0.05 + Math.random() * 0.25) * H;
  const angleRad = ((15 + Math.random() * 25) * Math.PI) / 180;
  const travelX = 1.3 * W;
  const dxPx = fromLeft ? travelX : -travelX;
  const dyPx = travelX * Math.tan(angleRad);
  const startX = fromLeft ? -160 : W + 160;
  return { startX, startY, dxPx, dyPx, startedAt: now };
}

function drawComet(ctx: CanvasRenderingContext2D, c: Comet, t: number) {
  const headX = c.startX + c.dxPx * t;
  const headY = c.startY + c.dyPx * t;

  // Trail opacity envelope (mirrors the previous CSS keyframe)
  let trailOpacity: number;
  if (t < 0.08) trailOpacity = t / 0.08;
  else if (t < 0.72) trailOpacity = 1;
  else if (t < 0.82) trailOpacity = 1 - ((t - 0.72) / 0.1) * 0.75;
  else if (t < 0.92) trailOpacity = 0.25 - ((t - 0.82) / 0.1) * 0.25;
  else trailOpacity = 0;

  if (trailOpacity > 0.01) {
    const trailLen = 150;
    const len = Math.hypot(c.dxPx, c.dyPx);
    const ux = c.dxPx / len;
    const uy = c.dyPx / len;
    const tailX = headX - ux * trailLen;
    const tailY = headY - uy * trailLen;

    const grad = ctx.createLinearGradient(tailX, tailY, headX, headY);
    grad.addColorStop(0, 'rgba(255,255,255,0)');
    grad.addColorStop(0.7, 'rgba(255,255,255,0.45)');
    grad.addColorStop(0.94, 'rgba(200,220,255,0.9)');
    grad.addColorStop(1, 'rgba(255,255,255,1)');

    ctx.save();
    ctx.globalAlpha = trailOpacity;
    ctx.shadowColor = 'rgba(255,255,255,0.85)';
    ctx.shadowBlur = 6;
    ctx.strokeStyle = grad;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(headX, headY);
    ctx.stroke();
    ctx.restore();
  }

  // Dispersion puff at the head — fades in late, scales up and blurs out
  let burnOpacity = 0;
  let burnScale = 0.4;
  let burnBlur = 0;
  if (t >= 0.78) {
    if (t < 0.85) {
      const k = (t - 0.78) / 0.07;
      burnOpacity = k * 0.55;
      burnScale = 0.4 + k * (1.1 - 0.4);
      burnBlur = k * 1;
    } else {
      const k = (t - 0.85) / 0.15;
      burnOpacity = (1 - k) * 0.55;
      burnScale = 1.1 + k * (2.4 - 1.1);
      burnBlur = 1 + k * 3;
    }
  }

  if (burnOpacity > 0.01) {
    const r = 18 * burnScale;
    const grad = ctx.createRadialGradient(headX, headY, 0, headX, headY, r);
    grad.addColorStop(0, 'rgba(255,255,255,0.85)');
    grad.addColorStop(0.35, 'rgba(220,230,255,0.45)');
    grad.addColorStop(0.65, 'rgba(255,255,255,0.15)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');

    ctx.save();
    ctx.globalAlpha = burnOpacity;
    ctx.filter = `blur(${burnBlur}px)`;
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(headX, headY, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

/**
 * Lets the rest of the auth screen drive the same reveal the 'r' key does —
 * the sign-in logo uses it. Module-scoped rather than context or props because
 * the reveal lives in a ref the render loop reads each frame, so there's no
 * state to share and only ever one NightBackground on the page. No-op until
 * the background mounts.
 */
let applyReveal: ((on: boolean) => void) | null = null;

export function setConstellationReveal(on: boolean) {
  applyReveal?.(on);
}

export function NightBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cometRef = useRef<Comet | null>(null);
  const startedAtRef = useRef<number>(0);
  const revealTargetRef = useRef<number>(0);
  const revealAlphaRef = useRef<number>(0);

  useEffect(() => {
    applyReveal = (on: boolean) => {
      revealTargetRef.current = on ? 1 : 0;
    };
    return () => {
      applyReveal = null;
    };
  }, []);

  // Hold-to-reveal: pressing 'r' brightens constellation stars while held.
  useEffect(() => {
    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };
    const onDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'r') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return; // don't hijack ctrl+r etc.
      if (e.repeat) return;
      if (isTypingTarget(e.target)) return;
      revealTargetRef.current = 1;
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'r') return;
      revealTargetRef.current = 0;
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);

  // Schedule comets — first one between 5s and 15s, then every 35s.
  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | undefined;

    const fire = () => {
      if (cancelled) return;
      cometRef.current = makeComet(performance.now());
      timeoutId = window.setTimeout(fire, COMET_INTERVAL_MS);
    };

    const firstDelay =
      FIRST_COMET_MIN_MS + Math.random() * (FIRST_COMET_MAX_MS - FIRST_COMET_MIN_MS);
    timeoutId = window.setTimeout(fire, firstDelay);

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  // Render loop — single rotated drawImage of a pre-rendered star field per frame.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rafId = 0;
    let starCanvas: HTMLCanvasElement | null = null;
    let revealCanvas: HTMLCanvasElement | null = null;
    let starSize = 0;
    let viewW = 0;
    let viewH = 0;
    startedAtRef.current = performance.now();

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      viewW = canvas!.clientWidth;
      viewH = canvas!.clientHeight;
      canvas!.width = Math.max(1, Math.floor(viewW * dpr));
      canvas!.height = Math.max(1, Math.floor(viewH * dpr));
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Pre-render stars onto a square offscreen canvas large enough that its
      // inscribed circle covers the viewport at any rotation angle.
      starSize = Math.ceil(Math.hypot(viewW, viewH) * 1.05);
      starCanvas = document.createElement('canvas');
      starCanvas.width = Math.floor(starSize * dpr);
      starCanvas.height = Math.floor(starSize * dpr);
      const sc = starCanvas.getContext('2d')!;
      sc.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintStarField(sc, starSize);

      // Reveal overlay — brighter constellation cores + halos, drawn on top
      // of starCanvas when the user holds 'r'.
      revealCanvas = document.createElement('canvas');
      revealCanvas.width = Math.floor(starSize * dpr);
      revealCanvas.height = Math.floor(starSize * dpr);
      const rc = revealCanvas.getContext('2d')!;
      rc.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintConstellationsRevealed(rc, starSize);
    }

    resize();
    window.addEventListener('resize', resize);

    function frame(now: number) {
      const angle = ((now - startedAtRef.current) / STAR_ROTATION_MS) * Math.PI * 2;

      ctx!.clearRect(0, 0, viewW, viewH);

      if (starCanvas) {
        ctx!.save();
        ctx!.translate(viewW / 2, viewH / 2);
        ctx!.rotate(angle);
        ctx!.drawImage(starCanvas, -starSize / 2, -starSize / 2, starSize, starSize);

        // Smoothly approach the reveal target each frame so press/release
        // fades in and out rather than snapping.
        const target = revealTargetRef.current;
        const cur = revealAlphaRef.current;
        revealAlphaRef.current = cur + (target - cur) * 0.12;
        if (revealCanvas && revealAlphaRef.current > 0.01) {
          ctx!.globalAlpha = revealAlphaRef.current;
          ctx!.drawImage(
            revealCanvas,
            -starSize / 2,
            -starSize / 2,
            starSize,
            starSize,
          );
          ctx!.globalAlpha = 1;
        }
        ctx!.restore();

        // Names — drawn upright outside the rotated transform so they don't spin.
        if (revealAlphaRef.current > 0.01) {
          paintConstellationNames(
            ctx!,
            viewW,
            viewH,
            starSize,
            angle,
            revealAlphaRef.current,
          );
        }
      }

      const c = cometRef.current;
      if (c) {
        const elapsed = now - c.startedAt;
        if (elapsed >= 0 && elapsed <= COMET_VISIBLE_MS) {
          drawComet(ctx!, c, elapsed / COMET_VISIBLE_MS);
        } else if (elapsed > COMET_VISIBLE_MS) {
          cometRef.current = null;
        }
      }

      rafId = requestAnimationFrame(frame);
    }
    rafId = requestAnimationFrame(frame);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      style={{ background: NIGHT_BG }}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
    </div>
  );
}
